import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Building2, ExternalLink, UserCog, X } from 'lucide-react';
import { toast } from 'sonner';
import Collection from '@model-core/core/Collection';
import { Avatar } from '@/components/Avatar';
import { AuditDate } from '@/components/AuditDate';
import { Box, Value } from '@/components/dashboard/DashboardParts';
import { ChangeCompanyRoleDialog, RemoveMembershipDialog } from '@/components/members/MembershipDialogs';
import { Button } from '@/components/ui/button';
import { DetailHeader } from '@/components/detail/DetailHeader';
import { DetailPageStatus } from '@/components/detail/DetailPageStatus';
import { useFollowEntityCompany } from '@/hooks/useFollowEntityCompany';
import { useLoader } from '@/hooks/useLoader';
import MemberModel from '@/models/entities/MemberModel';
import { ROLE_ADMIN } from '@/security/navigation';
import { useIsCurrentUser } from '@/security/useIsCurrentUser';
import { useAuthStore } from '@/stores/useAuthStore';
import { loadReferences } from '@/stores/useReferenceOptions';
import { companyRoleIcon } from '@/components/roles/roleIcons';
import { RoleLabel } from '@/components/roles/RoleLabel';

interface EmployeeDashboard {
  member: MemberModel;
  /** Ses rattachements aux autres sociétés */
  otherMembers: MemberModel[];
}

const BACK = { to: '/employees', label: 'Tous les employés' };

async function loadDashboard(id: string): Promise<EmployeeDashboard> {
  const { model: member } = await new MemberModel({ id }).fetch({ silentError: true });
  const { collection } = await new Collection(MemberModel)
    .setFilter('user', String(member.userId))
    .setSort('company.name')
    .list();
  // La liste contient l'employé lui-même : son utilisateur et sa société sont résolus avec les autres (une requête par
  // relation, au lieu d'une de plus pour lui)
  const self = collection.models.find((other) => String(other.id) === String(member.id));
  await Promise.all([
    collection.fetchResolvable('user'),
    collection.fetchResolvable('company'),
    ...(self ? [] : [member.fetchResolvable('user'), member.fetchResolvable('company')]),
    loadReferences('companyRoles'),
  ]);

  return {
    member: self ?? member,
    otherMembers: collection.models.filter((other) => String(other.id) !== String(member.id)),
  };
}

/**
 * Fiche d'un employé (/employees/{id} : id du rattachement Member), comme la fiche utilisateur :
 * en-tête (email, société, rôle dans la société, dates et auteurs ; actions Changer le rôle, Retirer de la société,
 * grisées sur son propre rattachement, lien vers sa fiche utilisateur) puis ses autres sociétés.
 */
export function EmployeeDashboardPage() {
  const { id = '' } = useParams();

  // Remontée à chaque employé (ex. lien vers son rattachement dans une autre société) : état et suivi de la société repartent de zéro
  return <EmployeeDashboard key={id} id={id} />;
}

function EmployeeDashboard({ id }: { id: string }) {
  const navigate = useNavigate();
  const isCurrentUser = useIsCurrentUser();
  // La fiche utilisateur est une page admin
  const isAdmin = useAuthStore((state) => !!state.user?.roles.includes(ROLE_ADMIN));
  const { data: dashboard, error, loaded, reload } = useLoader(id, loadDashboard);
  const [dialog, setDialog] = useState<'role' | 'remove' | null>(null);

  // La société de la sidebar suit l'employé affiché ; en choisir une autre ramène à la liste de ses employés
  useFollowEntityCompany(dashboard?.member.companyId, '/employees');

  if (!dashboard) {
    return <DetailPageStatus back={BACK} loaded={loaded} error={error} notFound="Employé introuvable." />;
  }

  const { member } = dashboard;

  // Son propre rattachement : ni changement de rôle ni retrait (la société garderait-elle un Dirigeant ?), refusés aussi par l'API
  const isMe = isCurrentUser(member.userId);
  const selfBlocker = isMe ? 'Vous ne pouvez pas modifier votre propre rattachement' : undefined;

  return (
    <div className="space-y-6">
      <DetailHeader
        back={BACK}
        label="Employé"
        avatar={<Avatar firstName={member.firstName} lastName={member.lastName} size="lg" tone="accent" />}
        title={member.fullName}
        subtitle={
          <p className="text-sm text-slate-500">
            <RoleLabel
              icon={companyRoleIcon(member.companyRoleReference)}
              label={`${member.companyRoleLabel} chez ${member.companyName}`}
              className="gap-2"
            />
          </p>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setDialog('role')} disabled={isMe} title={selfBlocker}>
              <UserCog className="w-4 h-4" />
              Changer le rôle
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDialog('remove')}
              disabled={isMe}
              title={selfBlocker}
            >
              <X className="w-4 h-4" />
              Retirer de la société
            </Button>
            {isAdmin && (
              <Button variant="outline" asChild>
                <Link to={`/users/${member.userId}`}>
                  <ExternalLink className="w-4 h-4" />
                  Fiche utilisateur
                </Link>
              </Button>
            )}
          </>
        }
      >
        <Value label="Email">{member.email}</Value>
        <Value label="Société">{member.companyName}</Value>
        <Value label="Rôle dans la société">
          <RoleLabel icon={companyRoleIcon(member.companyRoleReference)} label={member.companyRoleLabel} />
        </Value>
        <Value label="Ajouté le">
          <AuditDate date={member.createdAt} by={member.createdBy} />
        </Value>
        <Value label="Date de dernière modification">
          <AuditDate date={member.updatedAt} by={member.updatedBy} />
        </Value>
      </DetailHeader>

      <Box
        title="Autres sociétés"
        icon={Building2}
        empty={dashboard.otherMembers.length === 0 && `Employé uniquement chez ${member.companyName}.`}
      >
        <table className="w-full text-left text-sm" data-testid="employee-other-companies">
          <thead>
            <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="py-2">Société</th>
              <th className="py-2">Rôle</th>
              <th className="py-2">Ajouté le</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {dashboard.otherMembers.map((other) => (
              <tr key={other.id}>
                <td className="py-3">
                  <Link
                    to={`/employees/${other.id}`}
                    className="font-medium text-slate-900 hover:text-blue-700 hover:underline"
                  >
                    {other.companyName}
                  </Link>
                </td>
                <td className="py-3 text-slate-700">{other.companyRoleLabel}</td>
                <td className="py-3 text-slate-600">
                  <AuditDate date={other.createdAt} by={other.createdBy} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {dialog === 'role' && (
        <ChangeCompanyRoleDialog
          personName={member.fullName}
          member={member}
          onClose={() => setDialog(null)}
          onSaved={(roleLabel) => {
            toast.success(`${member.fullName} est maintenant ${roleLabel} chez ${member.companyName}.`);
            setDialog(null);
            reload();
          }}
        />
      )}

      {dialog === 'remove' && (
        <RemoveMembershipDialog
          personName={member.fullName}
          member={member}
          onClose={() => setDialog(null)}
          onRemoved={() => {
            toast.success(`${member.fullName} a été retiré de ${member.companyName}.`);
            navigate('/employees');
          }}
        />
      )}
    </div>
  );
}
